import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import StoryBody from "components/Library/StoryBody";
import { MicrodataContext } from "config/contexts";

import styles from "./PrimaryImage.css";

// Import the lazyloading module if the window context is defined.
if(typeof window !== "undefined") {
  import("lazysizes");
}

export default function PrimaryImage({ image, lazyload }) {
  if (!image || !Object.keys(image).length) { return null } // APIs are inconsistent between null and empty object

  // Handle entity maps, add "caption" type
  const captionBlocks = image.caption && image.caption.blocks && {
    ...image.caption,
    blocks: image.caption.blocks.map((block) => ({ ...block, type: "caption" })),
  };

  return (
    <MicrodataContext.Consumer>
      {(microdata) => {
        const figProps = microdata ? {
          itemProp: "image",
          itemScope: true,
          itemType: "http://schema.org/ImageObject",
        } : {};

        // for some reason the structured data tool can't find its way all the way down to the img tag, so this meta tag provides image data
        return (
          <figure {...figProps}>
            {microdata && <meta itemProp="url" content={image.crops["16x9"].url} />}
            <div className={styles.root}>
              <div className={styles.placeholder}>
                {lazyload ? (
                  <img className={classNames(styles.img, "lazyload")} data-src={image.crops["16x9"].url} alt={image.alt_text} />
                ) : (
                  <img className={styles.img} src={image.crops["16x9"].url} alt={image.alt_text} />
                )}
              </div>
            </div>
            {captionBlocks && (
              <figcaption className={styles.caption}>
                <StoryBody story={{ blocks: captionBlocks }} />
              </figcaption>
            )}
          </figure>
        );
      }}
    </MicrodataContext.Consumer>
  );
}

PrimaryImage.defaultProps = {
  lazyload: true,
};

PrimaryImage.propTypes = {
  lazyload: PropTypes.bool,
  image: PropTypes.shape({
    alt_text: PropTypes.string,
    caption: PropTypes.shape({
      blocks: PropTypes.array.isRequired,
      entityMap: PropTypes.array.isRequired,
    }),
    crops: PropTypes.shape({
      "16x9": PropTypes.shape({
        url: PropTypes.string.isRequired,
      }).isRequired,
    }),
  }),
};
